import { useEffect, useState } from 'react';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Navigation,
  Clock,
  Search,
  X,
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import useProviderTypeStore from '../store/providerTypeStore';
import useHealthAuthorityStore from '../store/healthAuthorityStore';
import useEmiratesStore from '../store/emiratesStore';
import useCityStore from '../store/cityStore';
import useAreaStore from '../store/areaStore';
import useRegistrationStore from '../store/useRegistrationStore';

import pharmacy from '../assets/pharmacy.png';
import medical_center from '../assets/medical_center.png';
import hospital from '../assets/hospital.png';
import optical from '../assets/optical.png';
import day_care from '../assets/day_care.png';
import diognostics from '../assets/diognostics.png';
import dental_clinic from '../assets/dental_clinic.png';
import dialysis_center from '../assets/dialysis_center.png';
import tele_medicine_provider from '../assets/tele_medicine_provider.png';
import traditional from '../assets/traditional.png';
import home_care from '../assets/home_care.png';

// Leaflet icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const customIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [28, 45],
  iconAnchor: [14, 45],
  popupAnchor: [1, -40],
  shadowSize: [50, 50],
});

// Searchable Dropdown Component
function SearchableDropdown({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  loading,
  labelKey = 'label',
  valueKey = 'value'
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownRef, setDropdownRef] = useState(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef && !dropdownRef.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef]);

  const filteredOptions = options.filter(option =>
    option[labelKey]?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(opt => opt[valueKey] === value);

  return (
    <div className="relative" ref={setDropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-5 py-3.5 bg-white border-2 border-gray-200 rounded-xl 
          focus:ring-4 focus:ring-primary/30 focus:border-primary 
          outline-none transition shadow-sm text-lg text-left
          flex items-center justify-between
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-300'}
        `}
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-400'}>
          {loading ? 'Loading...' : (selectedOption?.[labelKey] || placeholder)}
        </span>
        <Search size={18} className="text-gray-400" />
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200 sticky top-0 bg-white">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                autoFocus
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Options List */}
          <div className="overflow-y-auto max-h-64">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No results found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option[valueKey]}
                  type="button"
                  onClick={() => {
                    onChange(option[valueKey]);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`
                    w-full px-4 py-3 text-left hover:bg-primary/10 transition-colors
                    ${option[valueKey] === value ? 'bg-primary/20 text-primary font-medium' : 'text-gray-700'}
                  `}
                >
                  {option[labelKey]}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LocateMeControl({ onLocationSelect }) {
  const map = useMap();
  const [locating, setLocating] = useState(false);

  const handleLocate = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        map.setView([latitude, longitude], 17);
        onLocationSelect(L.latLng(latitude, longitude));
        setLocating(false);
      },
      () => {
        alert('Could not get your location. Please check permissions.');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  return (
    <button
      onClick={handleLocate}
      disabled={locating}
      className={`
        absolute top-4 right-4 z-[1000] flex items-center gap-2 px-5 py-2.5
        bg-white border border-gray-200 rounded-full
        shadow-md hover:shadow-lg transition-all text-sm font-medium
        ${locating ? 'text-primary' : 'text-gray-700 hover:text-primary'}
      `}
    >
      <Navigation size={18} />
      {locating ? 'Locating…' : 'My Location'}
    </button>
  );
}

function LocationMarker({ position, onSelect }) {
  useMapEvents({
    click(e) {
      onSelect(e.latlng);
    },
  });
  return position ? <Marker position={position} icon={customIcon} /> : null;
}

export default function ProviderRegistrationForm() {
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [marker, setMarker] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  // External stores
  const { providerTypes, loading: typesLoading, fetchProviderTypes } = useProviderTypeStore();
  const { healthAuthorities, loading: haLoading, fetchHealthAuthorities } = useHealthAuthorityStore();
  const { emirates, loading: emiratesLoading, fetchEmirates } = useEmiratesStore();
  const { areas, loading: areaLoading, fetchAreas, clearAreas } = useAreaStore();
  const { cities, loading: cityLoading, fetchCities, clearCities } = useCityStore();

  // Registration store - using Zustand
  const { providerData, updateProviderData } = useRegistrationStore();



  // Initialize data fetching
  useEffect(() => {
    if (providerTypes.length === 0) {
      fetchProviderTypes();
    }
  }, [fetchProviderTypes, providerTypes.length]);

  useEffect(() => {
    fetchHealthAuthorities();
  }, [fetchHealthAuthorities]);

  useEffect(() => {
    fetchEmirates();
  }, [fetchEmirates]);

  // Initialize selectedType from stored data
  useEffect(() => {
    if (providerData.providerType) {
      setSelectedType(providerData.providerType);
    }
  }, [providerData.providerType]);

  // Handle emirate changes
  useEffect(() => {
    if (providerData.emirates) {
      fetchCities(providerData.emirates);
    } else {
      clearCities();
    }
  }, [providerData.emirates, fetchCities, clearCities]);

  // Handle city changes
  useEffect(() => {
    if (providerData.city) {
      fetchAreas(providerData.city);
    } else {
      clearAreas();
    }
  }, [providerData.city, fetchAreas, clearAreas]);


  const typeImageMap = {
    1: medical_center,     // Medical Center
    2: pharmacy,           // Pharmacy
    3: diognostics,        // Diagnosis Center (note: fix typo in filename if needed → diagnostics)
    4: hospital,           // Hospital
    5: dental_clinic,      // Dental Clinic
    6: day_care,           // Day Care
    8: optical,            // Optical
    12: dialysis_center,
    13: tele_medicine_provider,
    14: traditional,
    15: home_care
  };

  const steps = [
    { num: 1, title: 'Facility Type' },
    { num: 2, title: 'Basic Info' },
    { num: 3, title: 'Location' },
    { num: 4, title: 'Operating Hours' },
  ];

  const reverseGeocode = async (lat, lon) => {
    setGeocoding(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`
      );
      const data = await res.json();

      if (data && data.display_name) {
        updateProviderData({
          location: data.display_name,
          street: data?.address?.road || data?.address?.neighbourhood || data?.address?.suburb || '',
        });
      }
    } catch (err) {
      console.error('Reverse geocode failed', err);
    } finally {
      setGeocoding(false);
    }
  };

  const handleLocationPick = (latlng) => {
    setMarker(latlng);
    updateProviderData({
      latitude: latlng.lat.toFixed(6),
      longitude: latlng.lng.toFixed(6),
    });
    reverseGeocode(latlng.lat, latlng.lng);
  };

  const canProceed = () => {
    if (step === 1) return !!selectedType;
    if (step === 2)
      return (
        providerData.facilityName.trim().length > 2 &&
        !!providerData.healthAuthority && providerData.landline.trim().length > 5
      );
    if (step === 3) {
      return (
        !!providerData.emirates &&
        !!providerData.city &&
        !!providerData.street
      );
    }
    return true;
  };

  const handleFinish = () => {
    window.location.href = '/#step-2';
  };

  // Transform data for searchable dropdowns
  const healthAuthorityOptions = healthAuthorities.map(ha => ({
    value: ha.HealthAuthority_ID,
    label: ha.HealthAuthority_Name
  }));

  const emiratesOptions = emirates.map(em => ({
    value: em.State_ID,
    label: em.StateName
  }));

  const cityOptions = cities.map(city => ({
    value: city.City_ID,
    label: city.CityName
  }));

  const areaOptions = areas.map(area => ({
    value: area.Area_ID,
    label: area.AreaName
  }));



  const renderStepContent = () => {
    switch (step) {
      case 1:
        if (typesLoading) return <p>Loading provider types...</p>;
        if (!providerTypes.length) return <p>No provider types available.</p>;

        return (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5">
            {providerTypes.map((type) => {
              const active = selectedType === type.providerType_ID;
              const imageSrc = typeImageMap[type.providerType_ID];
              return (
                <button
                  key={type.providerType_ID}
                  onClick={() => {
                    setSelectedType(type.providerType_ID);
                    updateProviderData({ providerType: type.providerType_ID });
                  }}
                  className={`
                    group relative bg-white border-2 border-gray-200 rounded-2xl p-5
                    shadow-md hover:shadow-xl transition-all duration-300
                    ${active ? 'ring-4 ring-primary border-primary shadow-primary/20' : ''}
                  `}
                >
                  {active && (
                    <div className="absolute -top-3 -right-3 bg-primary text-white rounded-full p-2 shadow-lg">
                      <Check size={18} />
                    </div>
                  )}
                  <div className="mb-4 flex justify-center">
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt={type.providerTypeName}
                        className="w-20 h-20 object-contain rounded-xl shadow-sm"
                      />
                    ) : (
                      // Fallback emoji or placeholder if no image is mapped
                      <div className="w-20 h-20 flex items-center justify-center text-5xl bg-gray-100 rounded-xl">
                        <img
                          src={hospital}
                          alt={type.providerTypeName}
                          className="w-20 h-20 object-contain rounded-xl shadow-sm"
                        />
                      </div>
                    )}
                  </div>
                  <h3 className={`font-semibold text-base ${active ? 'text-primary' : 'text-gray-800'}`}>
                    {type.providerTypeName}
                  </h3>
                </button>
              );
            })}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 max-w-2xl mx-auto">
            {/* Facility Name */}
            <div>
              <label className="block text-lg font-semibold text-gray-800 mb-2">Facility Name *</label>
              <input
                value={providerData.facilityName}
                onChange={(e) => updateProviderData({ facilityName: e.target.value })}
                placeholder="as per Health Authority Licence copy"
                className="w-full px-5 py-3.5 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-lg"
              />
            </div>

            {/* Health Authority - Now Searchable */}
            <div>
              <label className="block text-lg font-semibold text-gray-800 mb-2">Health Authority *</label>
              <SearchableDropdown
                options={healthAuthorityOptions}
                value={providerData.healthAuthority}
                onChange={(value) => updateProviderData({ healthAuthority: value })}
                placeholder="Select Health Authority"
                loading={haLoading}
                disabled={haLoading}
              />
            </div>

            {/* Website */}
            <div>
              <label className="block text-lg font-semibold text-gray-800 mb-2">Website</label>
              <input
                type="url"
                value={providerData.website || ''}
                onChange={(e) => updateProviderData({ website: e.target.value })}
                placeholder="https://"
                className="w-full px-5 py-3.5 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-lg"
              />
            </div>

            {/* Landline Number */}
            <div>
              <label className="block text-lg font-semibold text-gray-800 mb-2">
                Landline Number *
              </label>
              <input
                type="tel"
                value={providerData.landline || ''}
                onChange={(e) =>
                  updateProviderData({
                    landline: e.target.value.replace(/[^0-9+ ]/g, '')
                  })
                }
                placeholder="e.g. 971 4 123 4567"
                inputMode="numeric"
                className="w-full px-5 py-3.5 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-lg"
              />
            </div>


            {/* Is This a Branch? */}
            <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-6 shadow-md">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={providerData.isBranch}
                  onChange={(e) => updateProviderData({ isBranch: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <div>
                  <div className="font-semibold text-gray-800">Is This a Branch?</div>
                </div>
              </label>

              {providerData.isBranch && (
                <div className="mt-5">
                  <label className="block text-base font-semibold text-gray-600 mb-2">
                    Health Authority Licence Number of the Main Branch
                  </label>
                  <input
                    value={providerData.mainBranchHANumber || ''}
                    onChange={(e) => updateProviderData({ mainBranchHANumber: e.target.value })}
                    placeholder="HA-XXXXXX"
                    className="w-full px-5 py-3.5 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-lg"
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="space-y-5 order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="block text-lg font-semibold text-gray-800 mb-2">Country</label>
                  <select
                    value={providerData.country || 'uae'}
                    onChange={(e) => updateProviderData({ country: e.target.value })}
                    className="w-full px-5 py-3.5 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary shadow-sm text-lg"
                  >
                    <option value="uae">United Arab Emirates</option>
                  </select>
                </div>

                {/* Emirate - Now Searchable */}
                <div>
                  <label className="block text-lg font-semibold text-gray-800 mb-2">Emirate</label>
                  <SearchableDropdown
                    options={emiratesOptions}
                    value={providerData.emirates || ''}
                    onChange={(value) => {
                      updateProviderData({
                        emirates: value,
                        city: '',
                        area: '',
                      });
                    }}
                    placeholder="Select Emirate"
                    loading={emiratesLoading}
                    disabled={emiratesLoading}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-5">
                {/* City - Now Searchable */}
                <div>
                  <label className="block text-lg font-semibold text-gray-800 mb-2">City</label>
                  <SearchableDropdown
                    options={cityOptions}
                    value={providerData.city || ''}
                    onChange={(value) => {
                      updateProviderData({
                        city: value,
                        area: '',
                      });
                    }}
                    placeholder={cityLoading ? 'Loading cities...' : 'Select City'}
                    loading={cityLoading}
                    disabled={!providerData.emirates || cityLoading}
                  />
                </div>

                {/* Area - Now Searchable */}
                <div>
                  <label className="block text-lg font-semibold text-gray-800 mb-2">Area</label>
                  <SearchableDropdown
                    options={areaOptions}
                    value={providerData.area || ''}
                    onChange={(value) => updateProviderData({ area: value })}
                    placeholder={areaLoading ? 'Loading areas...' : 'Select Area'}
                    loading={areaLoading}
                    disabled={!providerData.city || areaLoading}
                  />
                </div>
              </div>

              {/* Street Address */}
              <div>
                <label className="block text-lg font-semibold text-gray-800 mb-2">Street Address</label>
                <input
                  value={providerData.street || ''}
                  onChange={(e) => updateProviderData({ street: e.target.value })}
                  placeholder={geocoding ? 'Fetching...' : 'e.g. Sheikh Zayed Road'}
                  className="w-full px-5 py-3.5 border-2 border-gray-200 rounded-xl shadow-sm text-lg bg-gray-50"
                />
              </div>

              {/* P.O. Box */}
              <div>
                <label className="block text-lg font-semibold text-gray-800 mb-2">P.O. Box</label>
                <input
                  value={providerData.poBox || ''}
                  onChange={(e) => updateProviderData({ poBox: e.target.value })}
                  placeholder="e.g. 12345"
                  className="w-full px-5 py-3.5 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-lg"
                />
              </div>

              {/* Full Location (from reverse geocoding) */}
              <div>
                <label className="block text-lg font-semibold text-gray-800 mb-2">Location</label>
                <input
                  value={providerData.location || ''}
                  onChange={(e) => updateProviderData({ location: e.target.value })}

                  placeholder={geocoding ? 'Fetching location...' : 'Auto-filled from map (e.g. Sheikh Zayed Road, Dubai)'}
                  className="w-full px-5 py-3.5 bg-white border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary/30 focus:border-primary outline-none transition shadow-sm text-lg"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Automatically filled from your map selection
                </p>
              </div>
            </div>

            {/* Map */}
            <div className="order-1 lg:order-2">
              <div className="bg-white border-2 border-gray-200 rounded-3xl shadow-xl overflow-hidden h-[460px] relative">
                <MapContainer center={[25.2048, 55.2708]} zoom={12} style={{ height: '100%', width: '100%' }}>
                  <TileLayer
                    url="https://mt2.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&scale=4"
                    attribution="&copy; Google Maps"
                    maxZoom={20}
                  />
                  <LocateMeControl onLocationSelect={handleLocationPick} />
                  <LocationMarker position={marker} onSelect={handleLocationPick} />
                </MapContainer>
              </div>
              <p className="mt-4 text-center text-gray-600">
                Click on the map or use "My Location" button
              </p>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-12 max-w-xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-primary flex items-center justify-center shadow-lg">
                <Clock size={32} className="text-white" />
              </div>
              <label className="block text-lg font-semibold text-gray-800 mb-3">Opens at</label>
              <input
                type="time"
                value={providerData.openTime || '09:00'}
                onChange={(e) => updateProviderData({ openTime: e.target.value })}
                className="px-8 py-4 bg-white border-2 border-gray-200 rounded-xl text-lg font-medium focus:ring-4 focus:ring-primary/30 focus:border-primary w-48 shadow-md"
              />
            </div>

            <div className="hidden sm:block text-primary text-4xl">→</div>

            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-primary flex items-center justify-center shadow-lg">
                <Clock size={32} className="text-white" />
              </div>
              <label className="block text-lg font-semibold text-gray-800 mb-3">Closes at</label>
              <input
                type="time"
                value={providerData.closeTime || '18:00'}
                onChange={(e) => updateProviderData({ closeTime: e.target.value })}
                className="px-8 py-4 bg-white border-2 border-gray-200 rounded-xl text-lg font-medium focus:ring-4 focus:ring-primary-dark/30 focus:border-primary-dark w-48 shadow-md"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-white to-primary/10 py-8 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Provider Empanelment
          </h1>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex gap-3 bg-gray-50 rounded-full shadow-lg p-2 border border-gray-200">
            {steps.map((s) => (
              <div
                key={s.num}
                className={`
                  flex items-center gap-3 px-6 py-3 rounded-full transition-all
                  ${step >= s.num ? 'bg-primary text-white shadow-md' : 'text-gray-500'}
                `}
              >
                <div className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center font-semibold">
                  {step > s.num ? <Check size={18} /> : s.num}
                </div>
                <div className="hidden sm:block">
                  <div className="font-medium">{s.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>



        {/* Main Form Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-gray-100">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-12 max-w-3xl mx-auto">
          <button
            onClick={() => setStep((s) => Math.max(1, s - 1))}
            disabled={step === 1}
            className={`
              flex items-center gap-3 px-8 py-4 rounded-full font-medium transition-all
              ${step === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'bg-white border-2 border-gray-300 hover:border-primary text-gray-700 hover:text-primary shadow-md'}
            `}
          >
            <ArrowLeft size={20} /> Back
          </button>

          <button
            onClick={() => {
              if (step === 4) handleFinish();
              else if (canProceed()) setStep((s) => s + 1);
            }}
            disabled={!canProceed()}
            className={`
              flex items-center gap-3 px-10 py-4 rounded-full font-semibold text-white transition-all shadow-lg hover:shadow-xl
              ${canProceed()
                ? 'bg-primary hover:bg-primary-dark'
                : 'bg-gray-400 cursor-not-allowed'}
            `}
          >
            {step === 4 ? 'Finish & Continue' : 'Next Step'}
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}